import React, { useContext, useMemo, useState } from 'react'
import { orderBy } from 'lodash'
import { Charts, GridTableStatusEnum, IGridTableColumn, NewGridTable, PrettyNumber } from '@ui'
import { PrintTitle, TabContext } from '../Details'
import { SimpleBar } from './SeatedEmployeesByDepartment'
import { Classes } from '@blueprintjs/core'
import classnames from 'classnames'
import { IPortfolioDepartment, Order } from '@api'

type Sorts = 'department.name' | 'areaSqft'

export default function AreaByDepartment() {
  const { object, units, loading } = useContext(TabContext)
  const scale = useMemo(() => Charts.colorScale(), [])
  const [sort, setSort] = useState<Sorts>('areaSqft')
  const [sortAscending, setSortAscending] = useState(false)

  let departments = object?.departments
  departments = orderBy(departments, sort, sortAscending ? 'asc' : 'desc') as IPortfolioDepartment[]

  const max = departments?.reduce(
    (max, d) => Math.max(max, units === 'sqft' ? d.areaSqft : d.areaSqrm),
    0
  )

  const columns: IGridTableColumn<IPortfolioDepartment & { id: string }>[] = [
    {
      label: 'Department',
      sort: 'department.name',
      Cell: ({ cell, skeleton }) => {
        return (
          <div className={classnames({ [Classes.SKELETON]: skeleton })}>
            {cell ? cell.data.department?.name : 'placeholder'}
          </div>
        )
      },
    },
    {
      sort: 'areaSqft',
      label: `Area ${units === 'sqft' ? 'Sqft' : 'Sqrm'}`,
      Cell: ({ cell, skeleton }) => {
        return (
          <div data-test="tab-unit" className={classnames({ [Classes.SKELETON]: skeleton })}>
            {cell ? (
              <PrettyNumber
                value={units === 'sqft' ? cell.data.areaSqft : cell.data.areaSqrm}
                numeralFormat="0,0"
              />
            ) : (
              'place'
            )}
          </div>
        )
      },
    },
    {
      label: '',
      Cell: ({ cell, skeleton }) => {
        if (cell?.data.id === 'total') return null

        return (
          <div className={classnames({ [Classes.SKELETON]: skeleton })}>
            {cell ? (
              <SimpleBar
                max={max}
                val={units === 'sqft' ? cell.data.areaSqft : cell.data.areaSqrm}
                barColor={`#${scale(cell.data.department?.name)}`}
              />
            ) : (
              ''
            )}
          </div>
        )
      },
    },
  ]

  const formatDepartments = (items: IPortfolioDepartment[]) => {
    return items.map((d) => ({
      data: { id: d.department?.id, ...d },
    }))
  }

  let formatedDepartments = formatDepartments(
    orderBy(departments, sort, sortAscending ? 'asc' : 'desc') as IPortfolioDepartment[]
  )

  formatedDepartments = [
    ...formatedDepartments,
    ...(object
      ? [
          {
            data: {
              id: 'total',
              allocated: object.allocated,
              areaSqft: object.areaSqft,
              areaSqrm: object.areaSqft,
              occupied: object.occupied,
              seatedCount: object.seatedCount,
              unoccupied: object.unoccupied,
              department: {
                id: 'total',
                name: 'Total',
                color: '',
              },
              teams: [],
            },
          },
        ]
      : []),
  ]

  return (
    <>
      <PrintTitle>
        <h3>Area By Department</h3>
      </PrintTitle>
      <NewGridTable<IPortfolioDepartment & { id: string }>
        status={loading ? GridTableStatusEnum.LOADING : GridTableStatusEnum.IDLE}
        columns={columns}
        maxCellWidth="auto"
        data={formatedDepartments}
        sort={{
          column: sort,
          order: sortAscending ? Order.ASC : Order.DESC,
          onSort: (column: string) => {
            if (sort === column) {
              return setSortAscending(!sortAscending)
            } else {
              setSort(column as Sorts)
              setSortAscending(false)
            }
          },
        }}
      />
    </>
  )
}
